import {
  type ICellRendererComp,
  type ICellRendererParams,
} from '@ag-grid-community/core'
import { renderToStaticMarkup } from 'react-dom/server'
import { Box, Row } from '../../Box'
import { isGroupRow } from './utils'

type ContractCellLogoData = {
  is_mev_bot: boolean | null
  is_erc20: boolean | null
  is_erc721: boolean | null
  is_erc1155: boolean | null
  is_smart_contract: boolean | null
  is_eoa: boolean | null
}

export class ContractCellRenderer
  implements ICellRendererComp<ContractCellLogoData>
{
  private template: ChildNode = getContractRowTemplate() as ChildNode
  private eGui?: HTMLElement

  // gets called once before the renderer is used
  init(params: ICellRendererParams<ContractCellLogoData>) {
    const contractSpritesExists = document.querySelector('#contract-sprites')
    if (!contractSpritesExists) {
      const sprite = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg',
      )
      sprite.id = 'contract-sprites'
      Object.assign(sprite.style, {
        display: 'none',
      })
      sprite.innerHTML = `<defs>
    <symbol id="contract-address" viewBox="0 0 16 16">
        <path fill-rule="evenodd" clip-rule="evenodd" d="m8 0 6.93 4v8L8 16l-6.93-4V4L8 0Zm-.65 8.35-3 3-.7-.7L6.29 8 3.65 5.35l.7-.7 3 3 .36.35-.36.35ZM8 11h4v-1H8v1Z" />
    </symbol>
    <symbol id="contract-bot" viewBox="0 0 16 16">
      <path fill-rule="evenodd" d="M5 2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v2h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-3H1a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h1V4a1 1 0 0 1 1-1h2V2Zm.5 7a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM12 7.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM11 11H5v1h6v-1Z" clip-rule="evenodd"/>
    </symbol>
    <symbol id="contract-creation" viewBox="0 0 16 16">
      <path d="M8 0a8 8 0 0 0-8 8.02A7.85 7.85 0 0 0 7.38 16v-4.9c0-1.35-1.1-2.46-2.46-2.46a.61.61 0 0 1-.61-.62c0-.35.27-.62.61-.62.95 0 1.81.37 2.46.96v-2.8c0-.35.28-.63.62-.63.34 0 .62.28.62.62v5.3a4.9 4.9 0 0 1 2.27-1.57c.33-.1.67.08.78.4.1.32-.09.67-.4.78a4 4 0 0 0-2.65 3.73V16A8 8 0 0 0 8 0Z"/>
    </symbol>
    <symbol id="contract-erc20" viewBox="0 0 16 16">
      <path d="M13.7 2.4A7.98 7.98 0 0 1 2.4 13.7a.18.18 0 0 1-.05-.05A7.98 7.98 0 0 1 2.3 2.4l.05-.05a7.98 7.98 0 0 1 11.36.05ZM1.27 7.38h1.86a4.9 4.9 0 0 1 1-2.4L2.8 3.67a6.73 6.73 0 0 0-1.54 3.71Zm1.54 4.95 1.32-1.31a4.9 4.9 0 0 1-1-2.4H1.26a6.73 6.73 0 0 0 1.54 3.71Zm11.94-3.71h-1.86a4.9 4.9 0 0 1-1 2.4l1.32 1.31a6.73 6.73 0 0 0 1.54-3.71Zm-3.72 3.26a4.9 4.9 0 0 1-2.4 1v1.86a6.73 6.73 0 0 0 3.71-1.54l-1.31-1.32Zm-3.64 1a4.9 4.9 0 0 1-2.4-1L3.67 13.2a6.73 6.73 0 0 0 3.71 1.54v-1.86Zm-2.4-8.76a4.9 4.9 0 0 1 2.4-1V1.26A6.73 6.73 0 0 0 3.67 2.8l1.31 1.32Zm3.64-1c.87.1 1.7.45 2.4 1l1.31-1.32a6.73 6.73 0 0 0-3.71-1.54v1.86Zm3.26 1.86c.55.7.9 1.53 1 2.4h1.86a6.73 6.73 0 0 0-1.54-3.71l-1.32 1.31Z"/>
    </symbol>
    <symbol id="contract-nft" viewBox="0 0 16 16">
        <path fill-rule="evenodd" d="M14.93 4 8 0 1.07 4v8L8 16l6.93-4V4ZM2.53 6.18V10h.92V7.6h.06L4.9 10h1.07V6.18h-.91V8.6H5L3.6 6.18H2.52Zm4.43 0V10h.98V8.43h1.5v-.7h-1.5v-.85h1.72v-.7h-2.7Zm3.33.7v-.7h3.46v.7H12.5V10h-.97V6.88h-1.24Z" clip-rule="evenodd"/>
    </symbol>
    <symbol id="contract-wallet" viewBox="0 0 16 16">
      <path fill-rule="evenodd" d="M1 4.88a3 3 0 0 1 3-3h8a3 3 0 0 1 2.98 2.62H9.12a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h5.86A3 3 0 0 1 12 14.13H4a3 3 0 0 1-3-3V4.87Zm8.13.62H15v5H9.12a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1Zm2.5 3.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd"/>
    </symbol>
  </defs>`
      document.body.insertBefore(sprite, document.body.firstChild)
    }

    // create the cell
    this.eGui = this.template.cloneNode(true) as HTMLElement

    this.refresh(params)
  }

  getGui() {
    return this.eGui as HTMLElement
  }

  // gets called whenever the cell refreshes
  refresh(params: ICellRendererParams<ContractCellLogoData>) {
    if (!this.eGui) {
      return false
    }

    if (isGroupRow(params) || !params.data) {
      if (this.eGui.childNodes[0] instanceof HTMLElement) {
        this.eGui.childNodes[0].style.opacity = '0'
      }
      this.eGui.childNodes[1].textContent = ''
      return true
    }

    ;(this.eGui.childNodes[0] as HTMLElement)!
      .querySelector('use')!
      .setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'xlink:href',
        '#' + this.getContractLogo(params.data),
      )

    if (params.value) {
      this.eGui.childNodes[1].textContent = params.value
    } else {
      // Should not be necessary anymore due to valueGetter fallbacking to this
      this.eGui.childNodes[1].textContent = 'Unlabeled'
    }

    // return true to tell the grid we refreshed successfully
    return true
  }

  getContractLogo(data: ContractCellLogoData) {
    switch (true) {
      case data.is_eoa: {
        return 'contract-wallet'
      }
      case data.is_erc20: {
        return 'contract-erc20'
      }
      case data.is_erc721: {
        return 'contract-nft'
      }
      case data.is_erc1155: {
        return 'contract-nft'
      }
      case data.is_mev_bot: {
        return 'contract-bot'
      }
      case data.is_smart_contract:
      default: {
        return 'contract-address'
      }
    }
  }
}

function getContractRowTemplate() {
  if (typeof window === 'undefined') {
    return null
  }

  const parser = new DOMParser()

  const contractRowTemplate = parser.parseFromString(
    renderToStaticMarkup(
      <Row start>
        <Box
          as="svg"
          // @ts-ignore
          fill="currentColor"
          flexShrink={0}
          style={{ width: '16px', height: '16px' }}
          color="secondary"
          className="contract-logo"
        >
          <use />
        </Box>
        <span>%%VALUE%%</span>
      </Row>,
    ),
    'text/html',
  )

  return contractRowTemplate.body.firstChild as ChildNode
}
